/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { LocationPrediction } from "../../../../hooks/useFacilityLocationQueries";
import Plot from "../../../../plotly/Plot";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../utils";
import { toLocalGraphTime } from "../../../../utils/date";
import { Button } from "../../../Helper/Button";
import { attributeIsSet } from "../../../scenes/Tenants/Forms/flagConstants";
import { averageTimeBlocks, toLinearSpace, toLogSpace } from "./regressionUtils";
import { RIPENING_GRAPH_STYLES, ripeningGraphXAxis } from "./ripeningGraphConstants";

const isValidNumber = (number: any): boolean => (number > 0 || parseFloat(number) === 0) && number !== "" && number !== null && number !== undefined;

interface GroupRipeningGraphProps {
    groupLabel: string;
    groupPallets: any[];
    checks: any[];
    predictions: LocationPrediction[];
    fruit_type: string;
    minHeight?: string;
}

export function GroupRipeningGraph({
    groupLabel,
    groupPallets,
    checks,
    predictions,
    fruit_type,
    minHeight = "45rem",
}: GroupRipeningGraphProps) {
    const config = useConfig();
    const [ripeningFieldIndex, setRipeningFieldIndex] = useState(0);
    const [isLogScale, setIsLogScale] = useState(true);

    const fruit_config = config.get_fruit_type(fruit_type);
    const the_field = fruit_config?.ripening_fields?.[ripeningFieldIndex];

    useEffect(() => {
        if (the_field) {
            setIsLogScale(the_field?.default_log_scale || fruit_type === "avocado");
        }
    }, [the_field?.default_log_scale, fruit_type]);

    if (!the_field?.fieldname_getter) {
        return null;
    }

    const upper_bound_field = `${the_field.fieldname_getter}_upper_bound`;
    const lower_bound_field = `${the_field.fieldname_getter}_lower_bound`;

    // Get all checks for pallets in this group
    const groupPalletIds = groupPallets.map((p) => p.id);
    const groupChecks = checks.filter((check) => check.layer_ids?.some((id: string) => groupPalletIds.includes(id)));

    // Get all predictions for pallets in this group
    const groupPredictions = predictions.filter((pred) => groupPalletIds.includes(pred.layer_id));

    // Create mapping from layer_id to layer label
    const layerIdToLabel = new Map<string, string>();
    groupPallets.forEach((pallet) => {
        if (pallet.id && pallet.label) {
            layerIdToLabel.set(String(pallet.id), pallet.label);
        }
    });

    // Aggregate all checks into single series, sorted by date
    const aggregatedChecks = useMemo(() => {
        return groupChecks
            .filter((i: any) => isValidNumber(i[the_field.fieldname_getter]))
            .sort((a: any, b: any) => dayjs(a.created).valueOf() - dayjs(b.created).valueOf())
            .map((i: any) => {
                const baseValue = i[the_field.fieldname_getter];
                const upperBound = i[upper_bound_field];
                const lowerBound = i[lower_bound_field];

                return {
                    ...i,
                    // Convert to log space when log scale is enabled
                    [the_field.fieldname_getter]: isLogScale && isValidNumber(baseValue)
                        ? toLogSpace(Number(baseValue))
                        : baseValue,
                    [upper_bound_field]: isLogScale && isValidNumber(upperBound)
                        ? toLogSpace(Number(upperBound))
                        : upperBound,
                    [lower_bound_field]: isLogScale && isValidNumber(lowerBound)
                        ? toLogSpace(Number(lowerBound))
                        : lowerBound,
                };
            });
    }, [groupChecks, the_field.fieldname_getter, upper_bound_field, lower_bound_field, isLogScale]);

    // Build prediction data
    const predictionData = useMemo(() => {
        if (!groupPredictions.length) {
            return null;
        }

        const sortedPredictions = [...groupPredictions].sort(
            (a, b) => dayjs(a.ripening_date).valueOf() - dayjs(b.ripening_date).valueOf()
        );

        const lineX: string[] = [];
        const lineY: number[] = [];
        const upperX: string[] = [];
        const upperY: number[] = [];
        const lowerX: string[] = [];
        const lowerY: number[] = [];

        // Add last actual value as starting point for prediction
        if (aggregatedChecks.length > 0) {
            const lastCheck = aggregatedChecks[aggregatedChecks.length - 1];
            const lastTimestamp = toLocalGraphTime(lastCheck.created);
            const lastValue = lastCheck[the_field.fieldname_getter];
            const lastUpper = lastCheck[upper_bound_field];
            const lastLower = lastCheck[lower_bound_field];

            if (isValidNumber(lastValue)) {
                lineX.push(lastTimestamp);
                lineY.push(Number(lastValue));
            }
            if (isValidNumber(lastUpper)) {
                upperX.push(lastTimestamp);
                upperY.push(Number(lastUpper));
            }
            if (isValidNumber(lastLower)) {
                lowerX.push(lastTimestamp);
                lowerY.push(Number(lastLower));
            }
        }

        sortedPredictions.forEach((prediction) => {
            const predictionMoment = dayjs(prediction.ripening_date);
            if (!predictionMoment.isValid()) {
                return;
            }

            const isoDate = toLocalGraphTime(prediction.ripening_date);
            const linearValue = prediction.output?.[the_field.fieldname_getter];
            const linearUpper = prediction.output?.upper_limit;
            const linearLower = prediction.output?.lower_limit;

            if (isValidNumber(linearValue)) {
                const numericValue = isLogScale ? toLogSpace(Number(linearValue)) : Number(linearValue);
                lineX.push(isoDate);
                lineY.push(numericValue);
            }

            if (isValidNumber(linearUpper)) {
                const numericUpper = isLogScale ? toLogSpace(Number(linearUpper)) : Number(linearUpper);
                upperX.push(isoDate);
                upperY.push(numericUpper);
            }

            if (isValidNumber(linearLower)) {
                const numericLower = isLogScale ? toLogSpace(Number(linearLower)) : Number(linearLower);
                lowerX.push(isoDate);
                lowerY.push(numericLower);
            }
        });

        if (lineX.length === 0) {
            return null;
        }

        return { lineX, lineY, upperX, upperY, lowerX, lowerY };
    }, [groupPredictions, aggregatedChecks, the_field.fieldname_getter, isLogScale]);

    // Prepare data for the 3 scatter plots
    const actualX = aggregatedChecks.map((i: any) => toLocalGraphTime(i.created));
    const actualY = aggregatedChecks.map((i: any) => Number(i[the_field.fieldname_getter]));
    const actualUpperY = aggregatedChecks.map((i: any) => Number(i[upper_bound_field])).filter((v: any) => isValidNumber(v));
    const actualUpperX = aggregatedChecks.map((i: any) => (isValidNumber(i[upper_bound_field]) ? toLocalGraphTime(i.created) : null)).filter((v: any): v is string => v !== null);
    const actualLowerY = aggregatedChecks.map((i: any) => Number(i[lower_bound_field])).filter((v: any) => isValidNumber(v));
    const actualLowerX = aggregatedChecks.map((i: any) => (isValidNumber(i[lower_bound_field]) ? toLocalGraphTime(i.created) : null)).filter((v: any): v is string => v !== null);

    // Get layer labels for actual checks
    const actualLayerLabels = aggregatedChecks.map((i: any) => {
        const layerId = Array.isArray(i.layer_ids) ? i.layer_ids[0] : i.layer_id || i.layer_ids;
        return layerId ? layerIdToLabel.get(String(layerId)) || "" : "";
    });

    // Get prediction data separately
    const predictedLineX = predictionData?.lineX || [];
    const predictedLineY = predictionData?.lineY || [];
    const predictedUpperX = predictionData?.upperX || [];
    const predictedUpperY = predictionData?.upperY || [];
    const predictedLowerX = predictionData?.lowerX || [];
    const predictedLowerY = predictionData?.lowerY || [];

    // Get layer labels for predicted points
    const predictedLayerLabels = predictedLineX.map((x) => {
        // Find the prediction that matches this x value
        const prediction = groupPredictions.find((p) => toLocalGraphTime(p.ripening_date) === x);
        return prediction?.layer_id ? layerIdToLabel.get(String(prediction.layer_id)) || "" : "";
    });

    // Combine actual and predicted data with colors, symbols, and labels per point
    // Create data structures that include color, symbol, and layer label info
    const linePoints = [
        ...actualX.map((x, i) => ({ x, y: actualY[i], color: RIPENING_GRAPH_STYLES.marker.actualLine, symbol: "circle", layerLabel: actualLayerLabels[i] || "" })),
        ...predictedLineX.map((x, i) => ({ x, y: predictedLineY[i], color: RIPENING_GRAPH_STYLES.marker.predictedLine, symbol: "circle-open", layerLabel: predictedLayerLabels[i] || "" })),
    ].sort((a, b) => dayjs(a.x).valueOf() - dayjs(b.x).valueOf());

    const upperPoints = [
        ...actualUpperX.map((x) => {
            const checkIndex = actualX.indexOf(x);
            return { x, y: actualUpperY[actualUpperX.indexOf(x)], color: RIPENING_GRAPH_STYLES.marker.actualUpper, symbol: "circle", layerLabel: checkIndex >= 0 ? (actualLayerLabels[checkIndex] || "") : "" };
        }),
        ...predictedUpperX.map((x) => {
            const predIndex = predictedLineX.indexOf(x);
            return { x, y: predictedUpperY[predictedUpperX.indexOf(x)], color: RIPENING_GRAPH_STYLES.marker.predictedUpper, symbol: "circle-open", layerLabel: predIndex >= 0 ? (predictedLayerLabels[predIndex] || "") : "" };
        }),
    ].sort((a, b) => dayjs(a.x).valueOf() - dayjs(b.x).valueOf());

    const lowerPoints = [
        ...actualLowerX.map((x) => {
            const checkIndex = actualX.indexOf(x);
            return { x, y: actualLowerY[actualLowerX.indexOf(x)], color: RIPENING_GRAPH_STYLES.marker.actualLower, symbol: "circle", layerLabel: checkIndex >= 0 ? (actualLayerLabels[checkIndex] || "") : "" };
        }),
        ...predictedLowerX.map((x) => {
            const predIndex = predictedLineX.indexOf(x);
            return { x, y: predictedLowerY[predictedLowerX.indexOf(x)], color: RIPENING_GRAPH_STYLES.marker.predictedLower, symbol: "circle-open", layerLabel: predIndex >= 0 ? (predictedLayerLabels[predIndex] || "") : "" };
        }),
    ].sort((a, b) => dayjs(a.x).valueOf() - dayjs(b.x).valueOf());

    // Extract arrays for markers (with colors, symbols, and layer labels)
    const allLineX = linePoints.map((p) => p.x);
    const allLineY = linePoints.map((p) => p.y);
    const allLineColors = linePoints.map((p) => p.color);
    const allLineSymbols = linePoints.map((p) => p.symbol);
    const allLineLayerLabels = linePoints.map((p) => p.layerLabel);

    const allUpperX = upperPoints.map((p) => p.x);
    const allUpperY = upperPoints.map((p) => p.y);
    const allUpperColors = upperPoints.map((p) => p.color);
    const allUpperSymbols = upperPoints.map((p) => p.symbol);

    const allLowerX = lowerPoints.map((p) => p.x);
    const allLowerY = lowerPoints.map((p) => p.y);
    const allLowerColors = lowerPoints.map((p) => p.color);
    const allLowerSymbols = lowerPoints.map((p) => p.symbol);

    // Extract sorted arrays for regression (without colors)
    const sortedLineX = allLineX;
    const sortedLineY = allLineY;
    const sortedUpperX = allUpperX;
    const sortedUpperY = allUpperY;
    const sortedLowerX = allLowerX;
    const sortedLowerY = allLowerY;

    // Create graph traces
    // Order matters: fill first (behind), then lines (on top)
    const traces: any[] = [];

    // Helper function to convert averaged x values (in days) back to local timestamps
    // Note: averaged x values are the average x of each cluster
    const convertAveragedXToTimestamps = (averagedXNumeric: number[], t0: number): string[] => {
        return averagedXNumeric.map((xNorm) => {
            const xTimestamp = t0 + xNorm * 86400000;
            return toLocalGraphTime(xTimestamp);
        });
    };

    // Calculate regression lines first (needed for fill)
    const upperRegressionX: string[] = [];
    const upperRegressionY: number[] = [];
    const lowerRegressionX: string[] = [];
    const lowerRegressionY: number[] = [];

    // Calculate upper averaged points (clustered by proximity, ~2-4 hours)
    if (sortedUpperX.length >= 2) {
        const timestamps = sortedUpperX.map((x) => dayjs(x).valueOf());
        const t0 = Math.min(...timestamps);
        // Normalize timestamps to relative days
        const xNumeric = timestamps.map((t) => (t - t0) / 86400000);
        const yNumeric = sortedUpperY;
        // Cluster nearby points and average x and y within each cluster
        const { xValues: averagedXNumeric, yValues: averagedYNumeric } = averageTimeBlocks(xNumeric, yNumeric);

        if (averagedXNumeric.length >= 2) {
            upperRegressionX.push(...convertAveragedXToTimestamps(averagedXNumeric, t0));
            upperRegressionY.push(...averagedYNumeric);
        }
    }

    // Calculate lower averaged points (clustered by proximity, ~2-4 hours)
    if (sortedLowerX.length >= 2) {
        const timestamps = sortedLowerX.map((x) => dayjs(x).valueOf());
        const t0 = Math.min(...timestamps);
        // Normalize timestamps to relative days
        const xNumeric = timestamps.map((t) => (t - t0) / 86400000);
        const yNumeric = sortedLowerY;
        // Cluster nearby points and average x and y within each cluster
        const { xValues: averagedXNumeric, yValues: averagedYNumeric } = averageTimeBlocks(xNumeric, yNumeric);

        if (averagedXNumeric.length >= 2) {
            lowerRegressionX.push(...convertAveragedXToTimestamps(averagedXNumeric, t0));
            lowerRegressionY.push(...averagedYNumeric);
        }
    }

    // 0. Fill area between regression lines (render first, behind everything)
    if (upperRegressionX.length > 0 && lowerRegressionX.length > 0) {
        // Use the same x-axis range for both
        const minX = Math.min(
            dayjs(upperRegressionX[0]).valueOf(),
            dayjs(lowerRegressionX[0]).valueOf()
        );
        const maxX = Math.max(
            dayjs(upperRegressionX[upperRegressionX.length - 1]).valueOf(),
            dayjs(lowerRegressionX[lowerRegressionX.length - 1]).valueOf()
        );
        const numPoints = 100;
        const fillX: string[] = [];
        const fillUpperY: number[] = [];
        const fillLowerY: number[] = [];

        for (let i = 0; i <= numPoints; i++) {
            const x = minX + (maxX - minX) * (i / numPoints);
            const xStr = toLocalGraphTime(x);

            // Find corresponding y values from averaged points
            const upperIdx = upperRegressionX.findIndex((ux) => dayjs(ux).valueOf() >= x);
            const lowerIdx = lowerRegressionX.findIndex((lx) => dayjs(lx).valueOf() >= x);

            let upperY: number;
            let lowerY: number;

            if (upperIdx === 0) {
                [upperY] = upperRegressionY;
            } else if (upperIdx > 0) {
                // Interpolate
                const x1 = dayjs(upperRegressionX[upperIdx - 1]).valueOf();
                const x2 = dayjs(upperRegressionX[upperIdx]).valueOf();
                const y1 = upperRegressionY[upperIdx - 1];
                const y2 = upperRegressionY[upperIdx];
                upperY = y1 + ((y2 - y1) * (x - x1)) / (x2 - x1);
            } else {
                upperY = upperRegressionY[upperRegressionY.length - 1];
            }

            if (lowerIdx === 0) {
                [lowerY] = lowerRegressionY;
            } else if (lowerIdx > 0) {
                // Interpolate
                const x1 = dayjs(lowerRegressionX[lowerIdx - 1]).valueOf();
                const x2 = dayjs(lowerRegressionX[lowerIdx]).valueOf();
                const y1 = lowerRegressionY[lowerIdx - 1];
                const y2 = lowerRegressionY[lowerIdx];
                lowerY = y1 + ((y2 - y1) * (x - x1)) / (x2 - x1);
            } else {
                lowerY = lowerRegressionY[lowerRegressionY.length - 1];
            }

            fillX.push(xStr);
            fillUpperY.push(upperY);
            fillLowerY.push(lowerY);
        }

        // Create filled area trace (added first so it renders behind lines)
        traces.push({
            type: "scatter",
            x: [...fillX, ...fillX.slice().reverse()],
            y: [...fillUpperY, ...fillLowerY.slice().reverse()],
            fill: "toself",
            fillcolor: RIPENING_GRAPH_STYLES.colors.fill,
            line: { color: "rgba(255,255,255,0)", width: 0 },
            hoverinfo: "skip",
            showlegend: false,
            mode: "lines",
        });
    }

    // 1. Markers for all data points (actual + predicted) with colors and symbols per point
    if (allLineX.length > 0 && allLineX.length === allLineColors.length) {
        traces.push({
            type: "scatter",
            x: allLineX,
            y: allLineY,
            mode: "markers",
            name: `${the_field.label}`,
            marker: {
                color: allLineColors,
                symbol: allLineSymbols,
                size: RIPENING_GRAPH_STYLES.marker.size,
                line: { width: 0 },
            },
            showlegend: false,
            hovertemplate: allLineLayerLabels.some((l) => l)
                ? `<b>%{fullData.name}</b><br>%{customdata[0]}<br>Value: ${isLogScale ? "%{customdata[1]:.1f}" : "%{y:.2f}"}<br>Date: %{x}<br><extra></extra>`
                : `<b>%{fullData.name}</b><br>Value: ${isLogScale ? "%{customdata[1]:.1f}" : "%{y:.2f}"}<br>Date: %{x}<br><extra></extra>`,
            customdata: allLineX.map((x, i) => {
                const label = allLineLayerLabels[i] || "";
                const linearValue = isLogScale ? toLinearSpace(allLineY[i]) : allLineY[i];
                return [label, linearValue];
            }),
        });
    }

    // 1b. Filled zone from averaged clusters
    if (sortedLineX.length >= 2) {
        const timestamps = sortedLineX.map((x) => dayjs(x).valueOf());
        const t0 = Math.min(...timestamps);
        // Normalize timestamps to relative days
        const xNumeric = timestamps.map((t) => (t - t0) / 86400000);
        const yNumeric = sortedLineY;
        // Cluster nearby points and average x and y within each cluster (~2-4 hours)
        const { xValues: averagedXNumeric, yValues: averagedYNumeric } = averageTimeBlocks(xNumeric, yNumeric);

        if (averagedXNumeric.length >= 2) {
            // Convert averaged x values back to timestamps (x is average of cluster)
            const averagedXTimestamps = convertAveragedXToTimestamps(averagedXNumeric, t0);

            traces.push({
                type: "scatter",
                x: averagedXTimestamps,
                y: averagedYNumeric,
                mode: "lines",
                name: `${the_field.label} (trend)`,
                line: {
                    color: RIPENING_GRAPH_STYLES.marker.actualLine,
                    width: RIPENING_GRAPH_STYLES.lineWidth.trend,
                    shape: "linear", // Use linear since we're already generating smooth points
                },
                showlegend: false,
                hoverinfo: "skip",
            });
        }
    }

    // 2. Upper bound markers (actual + predicted) with colors and symbols per point
    if (allUpperX.length > 0 && allUpperX.length === allUpperColors.length) {
        traces.push({
            type: "scatter",
            x: allUpperX,
            y: allUpperY,
            mode: "markers",
            name: `Upper ${the_field.label}`,
            marker: {
                color: allUpperColors,
                symbol: allUpperSymbols,
                size: RIPENING_GRAPH_STYLES.marker.upperLowerSize,
                line: { width: 0 },
            },
            showlegend: false,
            hoverinfo: "skip",
        });
    }

    // 3. Lower bound markers (actual + predicted) with colors and symbols per point
    if (allLowerX.length > 0 && allLowerX.length === allLowerColors.length) {
        traces.push({
            type: "scatter",
            x: allLowerX,
            y: allLowerY,
            mode: "markers",
            name: `Lower ${the_field.label}`,
            marker: {
                color: allLowerColors,
                symbol: allLowerSymbols,
                size: RIPENING_GRAPH_STYLES.marker.upperLowerSize,
                line: { width: 0 },
            },
            showlegend: false,
            hoverinfo: "skip",
        });
    }

    // Calculate range
    const allYValues = [
        ...sortedLineY,
        ...sortedUpperY,
        ...sortedLowerY,
    ].filter((v) => isValidNumber(v));

    const maxRangeValue = allYValues.length ? Math.max(...allYValues, 0) : 0;
    const range = [0, maxRangeValue > 0 ? Math.ceil(maxRangeValue * 1.06) : 1];

    // Generate tick values for log scale
    function generateTickValues(maxLogValue: number): number[] {
        const linearTicks = [0.5, 1, 2, 3, 5, 10, 15, 20, 30, 40, 50];
        const logTicks = linearTicks.map(toLogSpace).filter((v) => v <= maxLogValue && v >= 0);
        if (logTicks.length === 0) {
            return [0, Math.max(0.1, maxLogValue)];
        }
        const result = [0, ...logTicks];
        if (!result.includes(maxLogValue)) {
            result.push(maxLogValue);
        }
        return result.sort((a, b) => a - b);
    }

    const layout = {
        autosize: true,
        showlegend: false,
        dragmode: false,
        hovermode: "closest",
        plot_bgcolor: "#f9f9f9",
        paper_bgcolor: "#f9f9f9",
        margin: {
            l: 50,
            r: 10,
            t: 50,
            b: 50,
            pad: 0
        },
        yaxis: {
            fixedrange: true,
            title: the_field.label,
            showgrid: false,
            type: "linear",
            range,
            autorange: the_field.higher_value_is_more_ripe ? "reversed" : false,
            ...(isLogScale ? {
                tickmode: "array",
                tickvals: generateTickValues(range[1]),
                ticktext: generateTickValues(range[1]).map((v) => toLinearSpace(v).toFixed(1)),
            } : {}),
        },
        xaxis: ripeningGraphXAxis(traces),
        shapes: [],
        annotations: [],
    };

    // Add categories as swimming lanes
    const filteredCategories = getFilledArrayOrDefault(the_field.categories).filter((i: any) => (attributeIsSet(i.gte) || attributeIsSet(i.gt)) && (attributeIsSet(i.lte) || attributeIsSet(i.lt)));

    if (isFilledArray(filteredCategories)) {
        const categories = filteredCategories.map((i: any) => {
            const cat = {
                ...i,
                label: i.label,
                lower: parseFloat(i.gt || i.gte || 0),
                upper: parseFloat(i.lt || i.lte || 100),
            };
            if (isLogScale) {
                cat.log_upper = toLogSpace(cat.upper);
                cat.log_lower = toLogSpace(Math.max(0.1, cat.lower));
            } else {
                cat.log_upper = cat.upper;
                cat.log_lower = cat.lower;
            }
            cat.middle = (cat.lower + cat.upper) / 2;
            cat.log_middle = (cat.log_lower + cat.log_upper) / 2;
            return cat;
        });

        const swimmingLanes = categories.map((category) => ({
            type: "rect",
            x0: 0,
            x1: 1,
            xref: "paper",
            yref: "y",
            y0: isLogScale ? category.log_lower : category.lower,
            y1: isLogScale ? category.log_upper : category.upper,
            fillcolor: category.color || "#D3D3D3",
            opacity: 0.2,
            line: { width: 0 },
            layer: "below",
        }));

        const annotations = categories.map((category) => ({
            x: 0,
            xref: "paper",
            y: isLogScale ? category.log_middle : category.middle,
            yref: "y",
            text: category.label,
            showarrow: false,
            font: { size: 12, color: "#4C4E52" },
            xanchor: "left",
            yanchor: "middle",
            position: "left"
        }));

        layout.shapes = swimmingLanes as any;
        layout.annotations = annotations as any;
        layout.yaxis = {
            ...layout.yaxis,
            visible: false,
        } as any;
        layout.margin = { l: 0, r: 0, t: 50, b: 50, pad: 0 };

        const category_low = Math.min(
            layout.yaxis.range[0],
            ...categories.map((i: any) => (isLogScale ? i.log_lower : i.lower))
        );
        const category_high = Math.max(
            layout.yaxis.range[1],
            ...categories.map((i: any) => (isLogScale ? i.log_upper : i.upper))
        );
        layout.yaxis.range = [category_low, category_high];
    }

    const hasData = aggregatedChecks.length > 0 || (predictionData && predictionData.lineX.length > 0);

    return (
        <>
            <h4 className="mb-3">{groupLabel || "Ungrouped"}</h4>

            {getFilledArrayOrDefault(fruit_config?.ripening_fields).length > 1 && (
                <div className="d-flex flex-wrap justify-content-start gap-2 mb-2">
                    <>
                        {fruit_config?.ripening_fields?.map((field, index) => (
                            <Button
                                key={index}
                                outline={index !== ripeningFieldIndex}
                                size="sm"
                                color="secondary"
                                onClick={() => setRipeningFieldIndex(index)}
                            >
                                {field.label}
                            </Button>
                        ))}
                    </>
                </div>
            )}

            <div
                css={css`
                    min-height: ${minHeight};
                    .plotly .cursor-crosshair,
                    .plotly .cursor-drag,
                    .plotly .draglayer,
                    .plotly .nsewdrag,
                    .plotly .ewdrag,
                    .plotly .nsdrag {
                        cursor: crosshair !important;
                    }
                `}
                className="pt-2"
            >
                {!hasData && (
                    <div
                        className="text-center d-flex align-items-center justify-content-center"
                        css={css`
                            min-height: 24rem;
                            background-color: #f8f8f8;
                        `}
                    >
                        <p className="text-muted mb-0">
                            No data available<br></br>Start a check to track {the_field.label} progress
                        </p>
                    </div>
                )}
                {hasData && (
                    <Plot
                        data={traces as any}
                        useResizeHandler={true}
                        style={{ width: "100%", height: "100%", minHeight }}
                        layout={layout as any}
                        config={{
                            displayModeBar: false,
                            displaylogo: false,
                            responsive: true,
                            scrollZoom: false,
                            doubleClick: false,
                            doubleClickDelay: 0,
                        }}
                    />
                )}
            </div>
            {hasData && fruit_type !== "avocado" && (
                <div className="pt-2">
                    <Button
                        size="sm"
                        color="secondary"
                        outline={!isLogScale}
                        onClick={() => setIsLogScale((prev) => !prev)}
                    >
                        Log scale
                    </Button>
                </div>
            )}
        </>
    );
}

